import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { toast } from "react-hot-toast";
import { buyTickets } from "../../redux/slices/ticketSlice";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { getPurchaseTickets } from "../../redux/slices/purchaseTicketsSlice";
import { FaTimes } from "react-icons/fa";
import { fetchWalletData } from "../../redux/slices/walletSlice";
import Button from "../common/button";

const BuyTicket = ({
  ticket_id,
  user_id,
  setModalOpen,
  price,
  ticketCount,
}) => {
  const [quantity, setQuantity] = useState(1);
  const [isDisabled, setIsDisabled] = useState(false);

  // Dependencies and Utilities
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { t } = useTranslation(); // Translation hook

  // Redux Store Data
  const { balance } = useSelector((state) => state.wallet);
  const { loading2 } = useSelector((state) => state.tickets);

  // Fetch wallet data when user_id changes
  useEffect(() => {
    if (user_id) {
      dispatch(fetchWalletData(user_id)); // Fetch wallet data on component mount
    }
  }, [user_id, dispatch]);

  // Handle manual input changes with validation
  const handleInputChange = (e) => {
    const value = e.target.value;

    // Only update if the value is a valid number
    if (!isNaN(value)) {
      setQuantity(Number(value)); // Ensure quantity is always a number
    }
  };

  // Adjust quantity with boundary checks
  const changeQuantity = (amount) => {
    setQuantity((prev) => {
      const newQuantity = Math.max(0, (prev || 0) + amount); // Ensure quantity doesn't go below 1
      return newQuantity;
    });
  };

  // Show insufficient balance error
  const showBalanceToast = () => {
    toast.error(t("buyTickets.insufficientBalance"), {
      position: "top-center",
      autoClose: 3000,
    });
    setIsDisabled(true);
    setTimeout(() => {
      setIsDisabled(false); // Re-enable button after 2 seconds
    }, 3000);
  };

  // Main purchase handler
  const handleBuy = async () => {
    // Validation checks
    if (!quantity || quantity <= 0) {
      toast.error(t("buyTickets.selectTicket"));
      return;
    }

    const totalPrice = quantity * price;

    // Balance validation
    if (totalPrice > balance) {
      showBalanceToast();
      return;
    }

    // Prepare payload for API
    const payload = {
      user_id: String(user_id),
      ticket_number: String(ticket_id),
      ticket_quantity: String(quantity),
    };

    try {
      setIsDisabled(true); // Disable button

      // Dispatch purchase action
      await dispatch(buyTickets(payload)).unwrap();
      toast.success(t("buyTickets.success"));
      navigate("/quiz");
      dispatch(getPurchaseTickets(user_id));
      dispatch(fetchWalletData(user_id));
    } catch (error) {
      toast.error(error || t("buyTickets.error"));
    } finally {
      setTimeout(() => {
        setIsDisabled(false); // Re-enable button after 2 seconds
      }, 2000);
    }
  };

  return (
    <>
      {/* Modal Close Button */}
      <div className="flex justify-between items-center relative">
        <button
          onClick={() => setModalOpen(false)}
          className="text-red-600 hover:text-red-800 text-3xl absolute top-2 right-4 sm:top-3 sm:right-6 transition duration-300 ease-in-out"
        >
          <FaTimes />
        </button>
      </div>

      {/* Main Modal Content */}
      <div
        className="flex flex-col items-center justify-center p-7 bg-[#0A0A0A] rounded-3xl shadow-2xl text-[#E7B76A] w-full sm:w-96 md:w-full mx-auto"
        style={{
          boxShadow:
            "0px 4px 15px #EBC377, 0px -4px 15px #EBC377, 4px 0px 15px #EBC377, -4px 0px 15px #EBC377",
        }}
      >
        {/* Header Section */}
        <h2 className="lg:text-xl text-2xl font-bold mt-4 mb-3 text-center tracking-wider text-[#E7B76A]">
          {t("buyTickets.title")}
        </h2>

        {/* Ticket ID Display */}
        <div className="relative mb-2">
          <img
            src="/images/bubble.png"
            alt="ticket"
            className="w-12 h-12 rounded-full shadow-md animate-pulse"
          />
          <span className="absolute inset-0 flex items-center justify-center text-2xl font-extrabold text-black">
            {ticket_id}
          </span>
        </div>

        {/* Purchased Tickets Counter */}
        {ticketCount ? (
          <div className="text-center text-lg font-bold bg-gradient-to-r from-yellow-300 via-yellow-400 to-yellow-300 bg-clip-text text-transparent mb-1 max-w-64">
            {t("ticket_purchased")}{" "}
            <span className="inline-block bg-yellow-400 px-4 py-2 mx-1 rounded-full text-black shadow-lg hover:shadow-2xl transition-all transform hover:scale-110 animate-bounce font-bold text-lg">
              {ticketCount}
            </span>
            {t("ticket_purchased1")}
          </div>
        ) : (
          <div className="text-center text-gray-300 italic text-lg mb-1 max-w-64">
            {t("no_ticket_purchased")}
          </div>
        )}

        {/* Display Ticket Price */}
        <div className="mb-6 text-center">
          <h3 className="text-xl text-white font-semibold mb-2">
            {t("buyTickets.ticketPrice")}:{" "}
            <span className="text-yellow-400">₹{price}</span>
          </h3>
          <h3 className="text-xl text-white font-semibold">
            {t("buyTickets.totalPrice")}:{" "}
            <span className="text-green-500">₹{price * quantity}</span>
          </h3>
        </div>

        {/* Purchase Controls Container */}
        <div
          className="w-full bg-[#42423f] rounded-2xl p-4 mb-2"
          // style={{
          //   boxShadow: "0px 4px 15px #EBC377, 0px -4px 15px #EBC377, 4px 0px 15px #EBC377, -4px 0px 15px #EBC377"
          // }}
        >
          {/* Quantity Selector */}
          <div className="flex items-center justify-center gap-4 mb-3">
            <button
              onClick={() => changeQuantity(-1)}
              className="bg-red-600 hover:bg-red-700 text-white font-semibold py-3 px-6 rounded-full shadow-lg transition duration-300 ease-in-out"
            >
              -
            </button>
            <input
              type="text"
              id="quantity"
              inputMode="numeric"
              value={quantity}
              onChange={handleInputChange}
              className="text-center w-20 text-black text-lg font-semibold border-2 border-gray-400 rounded-lg focus:ring-2 focus:ring-yellow-400 py-2"
            />
            <button
              onClick={() => changeQuantity(1)}
              className="bg-green-600 hover:bg-green-700 text-white font-semibold py-3 px-6 rounded-full shadow-lg transition duration-300 ease-in-out"
            >
              +
            </button>
          </div>

          {/* Purchase Button with Loading State */}
          <Button
            onClick={handleBuy}
            disabled={loading2 || isDisabled}
            text={loading2 ? t("buyTickets.loading") : t("buyTickets.button")}
            newWidth="230px" // Add your desired maxWidth here
          />
        </div>
      </div>
    </>
  );
};

export default BuyTicket;
